import { Component, DestroyRef, effect, inject, signal, untracked, WritableSignal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { AnalyticsService } from '../../core/services/analytics.service';
import { DateRangeService } from '../../core/services/date-range.service';
import { Category, PagedResult, Severity, Verdict } from '../../core/models/common.models';
import { IssueListItem } from '../../core/models/issue.models';
import { LocalDatePipe } from '../../shared/pipes/local-date.pipe';
import { StatusChipComponent } from '../../shared/components/status-chip/status-chip.component';

@Component({
  selector: 'app-issue-explorer-page',
  standalone: true,
  imports: [FormsModule, LocalDatePipe, StatusChipComponent],
  templateUrl: './issue-explorer-page.component.html',
  styleUrl: './issue-explorer-page.component.scss',
})
export class IssueExplorerPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dateRange = inject(DateRangeService);
  private readonly destroyRef = inject(DestroyRef);
  private currentLoadId = 0;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly result = signal<PagedResult<IssueListItem> | null>(null);
  readonly search = signal('');
  readonly selectedSeverities = signal<Severity[]>([]);
  readonly selectedCategories = signal<Category[]>([]);
  readonly selectedVerdicts = signal<Exclude<Verdict, 'Failed'>[]>([]);
  readonly expandedIssueId = signal<string | null>(null);
  readonly severities: Severity[] = ['High', 'Medium', 'Low'];
  readonly categories: Category[] = ['Bug', 'Performance', 'Security', 'CleanCode', 'BestPractice'];
  readonly verdicts: Exclude<Verdict, 'Failed'>[] = ['Approved', 'NeedsChanges', 'Blocked'];

  constructor() {
    effect(() => {
      this.dateRange.appliedRange();
      this.dateRange.appliedRevision();
      untracked(() => this.load());
    });
  }

  load(): void {
    const loadId = ++this.currentLoadId;
    this.loading.set(true);
    this.error.set(null);
    this.result.set(null);

    this.analyticsService
      .getIssues({
        ...this.dateRange.toQueryParams(),
        search: this.search() || undefined,
        severity: this.selectedSeverities().length ? this.selectedSeverities() : undefined,
        category: this.selectedCategories().length ? this.selectedCategories() : undefined,
        verdict: this.selectedVerdicts().length ? this.selectedVerdicts() : undefined,
        page: 1,
        pageSize: 25,
        sortBy: 'reviewCompletedAt',
        sortDirection: 'desc',
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (loadId === this.currentLoadId) {
            this.loading.set(false);
          }
        }),
      )
      .subscribe({
        next: (response) => {
          if (loadId !== this.currentLoadId) {
            return;
          }

          try {
            if (!response?.paging || !Array.isArray(response?.items)) {
              throw new Error('Issues response is missing expected paging/items fields.');
            }

            this.result.set(response);
          } catch (mappingError) {
            console.error('Issues response handling failed', mappingError, response);
            this.result.set(null);
            this.error.set('Issues response could not be rendered. Check the browser console for the payload.');
          }
        },
        error: (error: unknown) => {
          if (loadId !== this.currentLoadId) {
            return;
          }

          console.error('Issues request failed', error);
          this.error.set(this.formatError(error, 'Issues could not be loaded.'));
        },
      });
  }

  toggle(issueId: string): void {
    this.expandedIssueId.set(this.expandedIssueId() === issueId ? null : issueId);
  }

  private formatError(error: unknown, fallback: string): string {
    if ((error as { name?: string })?.name === 'TimeoutError') {
      return 'Issues request timed out after 8s. The backend is not responding for the selected range.';
    }

    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return `Issues request could not reach the backend${error.url ? ` @ ${error.url}` : ''}. Check backend availability, CORS, and network access from the browser.`;
      }

      const details = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
      const requestUrl = error.url ? ` @ ${error.url}` : '';
      return `${fallback} (${error.status || 'network'}${requestUrl}: ${details || error.message})`;
    }

    return fallback;
  }

  toggleSelection<T>(signalValue: WritableSignal<T[]>, value: T): void {
    const current = signalValue();
    signalValue.set(current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  }

  isSelected<T>(signalValue: WritableSignal<T[]>, value: T): boolean {
    return signalValue().includes(value);
  }

}
